import { styled } from '@linaria/react';
import { fonts, layout, radius, semantic, shadow, size, space } from '../../theme/tokens';

export const Bar = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  border-radius: ${radius('input', 'radius-full')};
  background: ${semantic.bg.surfaceSubtle};
  overflow: visible;
  min-height: 56px;
  padding: 0 ${space['space-100']};
  --jp-pill-fg: ${semantic.text.primary};
  --jp-dot-fg: ${semantic.text.tertiary};

  @media screen and (max-width: 768px) {
    &[data-image-overlay='true'] {
      background: ${semantic.bg.overlayBlur};
      backdrop-filter: blur(${space['space-200']});
      --jp-pill-fg: ${semantic.text.inverse};
      --jp-dot-fg: ${semantic.text.inverse};
    }
  }

  @media screen and (max-width: 600px) {
    display: none;
  }
`;

export const DotSeparator = styled.span`
  color: var(--jp-dot-fg, ${semantic.text.tertiary});
  margin: 0 ${space['space-050']};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

export const CategoryWrapper = styled.div`
  position: relative;
  display: flex;
  height: calc(100% - 8px);
  margin: ${space['space-100']} 0;

  @media screen and (max-width: 600px) {
    display: none;
  }
`;

export const CategoryDropdown = styled.div`
  box-sizing: border-box;
  width: 100%;
  background: ${semantic.bg.canvas};
  border-radius: ${radius('overlay', 'radius-md')};
  box-shadow: ${shadow.lg};
  z-index: ${layout.zIndexDropdown};
  padding: ${space['space-300']};
  display: flex;
  flex-direction: column;
  gap: ${space['space-100']};
`;

export const DatesWrapper = styled.div<{ $isFirst?: boolean }>`
  position: relative;
  display: flex;
  height: calc(100% - 8px);
  border-radius: ${({ $isFirst }) =>
    $isFirst ? `${radius('input', 'radius-full')} 0 0 ${radius('input', 'radius-full')}` : '0'};
`;

export const GuestsWrapper = styled.div`
  position: relative;
  display: flex;
  height: calc(100% - 8px);
`;

export const SearchWrapper = styled.div`
  display: flex;
  height: calc(100% - 8px);
  margin: ${space['space-100']};
  margin-left: ${space['space-200']};
`;

export const SearchPill = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${space['space-100']};
  padding: ${space['space-300']} ${space['space-600']};
  background: ${semantic.bg.brand};
  border: none;
  border-radius: ${radius('button', 'radius-full')};
  color: ${semantic.text.onBrand};
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const SearchLabel = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;

  &::after {
    content: attr(data-text);
    font-weight: ${fonts.weight.semiBold};
    height: 0;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
  }
`;

export { PillLabel } from './JourneyPicker.styles';

export const DropdownAnchor = styled.div`
  position: fixed;
  z-index: ${layout.zIndexDropdown};

  &[data-wide='true'] {
    min-width: 400px;
  }
`;

export const RateDropdownShell = styled.div`
  box-sizing: border-box;
  width: 440px;
  padding: ${space['space-1000']};
  background: ${semantic.bg.canvas};
  border-radius: ${radius('overlay', 'radius-xxl')};
  box-shadow: ${shadow.lg};
`;

export const RateWrapper = styled.div`
  position: relative;
  display: flex;
  height: calc(100% - 8px);
  padding: 0 ${space['space-200']};
`;

export const RateLabelText = styled.span`
  display: inline-block;
  max-width: 10ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RatePillGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space['space-100']};
  height: 100%;
  padding: 0 ${space['space-300']} 0 ${space['space-600']};
  background: ${semantic.bg.canvas};
  border-radius: ${radius('chip', 'radius-full')};
`;

export const RatePillLabelButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${space['space-300']};
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};
  white-space: nowrap;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
    border-radius: ${radius('button', 'radius-xs')};
  }
`;

export const ClearRateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${space['space-600']};
  height: ${space['space-600']};
  border-radius: ${radius('button', 'radius-full')};
  background: transparent;
  color: ${semantic.text.secondary};
  cursor: pointer;
  font-size: ${fonts.size.md};
  line-height: 1;
  padding: 0;
  border: none;
  flex-shrink: 0;

  &:hover {
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const Pill = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${space['space-300']};
  background: ${({ $active }) => ($active ? semantic.bg.canvas : 'transparent')};
  border: none;
  cursor: pointer;
  padding: ${space['space-300']} ${space['space-600']};
  height: 100%;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${({ $active }) => ($active ? fonts.weight.semiBold : fonts.weight.medium)};
  color: ${({ $active }) =>
    $active ? semantic.text.primary : `var(--jp-pill-fg, ${semantic.text.primary})`};
  white-space: nowrap;
  border-radius: ${radius('chip', 'radius-full')};
  transition: background 0.15s ease, font-weight 0.1s ease;

  &:hover {
    font-weight: ${fonts.weight.semiBold};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;
